import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import styled from 'styled-components';
import {
  AutoSizer,
  CellMeasurer,
  CellMeasurerCache,
  InfiniteLoader,
  List,
} from 'react-virtualized';
import type {
  Index,
  InfiniteLoaderChildProps,
  ListRowRenderer,
} from 'react-virtualized';
import type { AppointmentData } from 'api/AnsweringOrgController/getAppointments';
import { Checkbox, Color, Icon, Spin, Text } from '@bb/elements-web';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { flagAppointment } from 'api/AppointmentController';
import type { EnhancedAppointment } from '@bb/domain/dist/types';
import { debounce } from 'lodash';
import {
  ActionColumnRender,
  AddressColumnRender,
  AgentColumnRender,
  DateColumnRender,
  StatusColumnRender,
} from './ColumnRenders';
import { applyFlaggedUpdateToAnsweringCache } from '../utils';

const DEFAULT_ROW_HEIGHT = 80;

export type SortColumn = 'address' | 'date' | 'agent' | 'status';
export type SortDirection = 'asc' | 'desc';

interface QueueListProps {
  data: AppointmentData[];
  isLoadingData?: boolean;
  hasMore?: boolean;
  isLoadingMore?: boolean;
  onLoadMore: () => Promise<any>;
  selectedRows: EnhancedAppointment[];
  viewingState: Record<string, string[]>;
  sortColumn: SortColumn;
  sortDirection: SortDirection;
  onSort: (column: SortColumn) => void;
  onToggleSelectedRow?: (appointment: EnhancedAppointment) => void;
  onToggleSelectAllRows?: (selectAll: boolean) => void;
  onViewAppointment: (appointment: EnhancedAppointment) => void;
  onFilter?: (filter: string) => void;
}

const QueueList: React.FC<QueueListProps> = ({
  data,
  isLoadingData,
  hasMore,
  isLoadingMore,
  onLoadMore,
  selectedRows,
  viewingState,
  sortColumn,
  sortDirection,
  onSort,
  onToggleSelectedRow,
  onToggleSelectAllRows,
  onViewAppointment,
  onFilter,
}) => {
  const [isReordering, setIsReordering] = useState(false);
  const listRef = useRef<List>(null);
  const registerChildRef = useRef<
    InfiniteLoaderChildProps['registerChild'] | null
  >(null);
  const [listHeight, setListHeight] = useState(
    Math.max(500, window.innerHeight - 255),
  );

  const cellCache = useMemo(
    () =>
      new CellMeasurerCache({
        defaultHeight: DEFAULT_ROW_HEIGHT,
        fixedWidth: true,
      }),
    [],
  );

  // Recalculate on window resize
  useEffect(() => {
    const computeHeight = () => {
      setListHeight(Math.max(500, window.innerHeight - 255));
      cellCache.clearAll();
      listRef.current?.recomputeRowHeights();
    };
    const debouncedCompute = debounce(computeHeight, 150);
    window.addEventListener('resize', debouncedCompute);
    return () => window.removeEventListener('resize', debouncedCompute);
  }, [cellCache]);

  // Clear measurements when data changes (reorder, flag, new pages)
  useEffect(() => {
    cellCache.clearAll();
    listRef.current?.recomputeRowHeights();
  }, [data, cellCache]);

  const listRefCallback = useCallback((el: List | null) => {
    registerChildRef.current?.(el);
    (listRef as React.MutableRefObject<List | null>).current = el;
  }, []);

  const queryClient = useQueryClient();
  const { mutate: flagAppointmentMutation } = useMutation({
    mutationFn: flagAppointment,
    onMutate: async ({ appointmentId, flagged }) => {
      setIsReordering(true);
      await queryClient.cancelQueries({ queryKey: ['answeringAppointments'] });

      const previousQueries = queryClient.getQueriesData({
        queryKey: ['answeringAppointments'],
      });

      queryClient.setQueriesData({ queryKey: ['answeringAppointments'] }, old =>
        applyFlaggedUpdateToAnsweringCache(old, { appointmentId, flagged }),
      );

      return { previousQueries };
    },
    onError: (_err, _vars, context) => {
      context?.previousQueries?.forEach(([queryKey, qData]) => {
        queryClient.setQueryData(queryKey, qData);
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['answeringAppointments'] });
      requestAnimationFrame(() => setIsReordering(false));
    },
  });

  const selectedRowIds = useMemo(
    () => selectedRows.map(app => app.id),
    [selectedRows],
  );

  const rowCount = hasMore ? data.length + 1 : data.length;

  const isRowLoaded = useCallback(
    ({ index }: Index) => !hasMore || index < data.length,
    [hasMore, data.length],
  );

  const loadMoreRows = useCallback(() => {
    if (isLoadingMore) return Promise.resolve();
    return onLoadMore();
  }, [isLoadingMore, onLoadMore]);

  const rowRenderer: ListRowRenderer = useCallback(
    ({ index, key, style, parent }) => {
      if (index >= data.length) {
        return (
          <CellMeasurer
            cache={cellCache}
            columnIndex={0}
            key={key}
            parent={parent}
            rowIndex={index}
          >
            <LoadingRow key={key} style={style}>
              <Spin dataCy="loading-more-spinner" size="small" />
              <span style={{ marginLeft: 8 }}>
                Loading more appointments...
              </span>
            </LoadingRow>
          </CellMeasurer>
        );
      }

      const { appointment, listing } = data[index];
      const isFlagged = appointment?.flagged;
      const isViewing = !!viewingState[appointment?.id];

      return (
        <CellMeasurer
          cache={cellCache}
          columnIndex={0}
          key={key}
          parent={parent}
          rowIndex={index}
        >
          {({ registerChild }) => (
            <RowOuter
              ref={registerChild as React.Ref<HTMLDivElement>}
              style={style}
              data-cy={`answering-queue-row-${appointment?.id}`}
              className={[
                isFlagged && 'flagged-row',
                isViewing && 'showing-row',
                isReordering && 'is-reordering',
              ]
                .filter(Boolean)
                .join(' ')}
              onClick={() => onViewAppointment(appointment)}
            >
              <RowInner>
                <Cell
                  $flex="0 0 50px"
                  data-cy="action-td"
                  onClick={e => e.stopPropagation()}
                >
                  <ActionColumnRender
                    appointment={appointment}
                    selectedRowIds={selectedRowIds}
                    onFlagAppointment={flagAppointmentMutation}
                    onToggleSelectedRow={onToggleSelectedRow}
                  />
                </Cell>
                <Cell $flex="2 1 150px" data-cy="address-td">
                  <AddressColumnRender listing={listing} onFilter={onFilter} />
                </Cell>
                <Cell $flex="1.5 1 150px" data-cy="date-td">
                  <DateColumnRender appointment={appointment} />
                </Cell>
                <Cell $flex="2 1 150px" data-cy="agent-td">
                  <AgentColumnRender
                    appointment={appointment}
                    onFilter={onFilter}
                  />
                </Cell>
                <Cell $flex="2 1 150px" data-cy="status-td">
                  <StatusColumnRender appointment={appointment} />
                </Cell>
              </RowInner>
            </RowOuter>
          )}
        </CellMeasurer>
      );
    },
    [
      data,
      viewingState,
      selectedRowIds,
      isReordering,
      onViewAppointment,
      onToggleSelectedRow,
      flagAppointmentMutation,
      onFilter,
      cellCache,
    ],
  );

  const isAllSelected = data.length > 0 && selectedRows.length === data.length;
  const isIndeterminate =
    selectedRows.length > 0 && selectedRows.length < data.length;

  if (isLoadingData && !data.length) {
    return (
      <CenteredContainer data-cy="answering-queue-table">
        <Spin dataCy="answering-queue-loading" size="large" />
      </CenteredContainer>
    );
  }

  if (!data.length && !isLoadingData) {
    return (
      <CenteredContainer data-cy="answering-queue-table">
        <Text color="grey">No appointments found</Text>
      </CenteredContainer>
    );
  }

  return (
    <Wrapper data-cy="answering-queue-table">
      <HeaderRow data-role="header">
        <HeaderCell $flex="0 0 50px" $centered>
          <Checkbox
            data-cy="select-all-checkbox"
            checked={isAllSelected}
            indeterminate={isIndeterminate}
            onChange={() => onToggleSelectAllRows?.(!isAllSelected)}
            onClick={(e: React.MouseEvent) => e.stopPropagation()}
          />
        </HeaderCell>
        <SortableHeaderCell
          $flex="2 1 150px"
          $iconPadding
          $active={sortColumn === 'address'}
          onClick={() => onSort('address')}
        >
          Address
          <SortIndicator
            column="address"
            active={sortColumn}
            direction={sortDirection}
          />
        </SortableHeaderCell>
        <SortableHeaderCell
          $flex="1.5 1 150px"
          $active={sortColumn === 'date'}
          onClick={() => onSort('date')}
        >
          Date
          <SortIndicator
            column="date"
            active={sortColumn}
            direction={sortDirection}
          />
        </SortableHeaderCell>
        <SortableHeaderCell
          $flex="2 1 150px"
          $active={sortColumn === 'agent'}
          onClick={() => onSort('agent')}
        >
          Agent
          <SortIndicator
            column="agent"
            active={sortColumn}
            direction={sortDirection}
          />
        </SortableHeaderCell>
        <SortableHeaderCell
          $flex="2 1 150px"
          $active={sortColumn === 'status'}
          onClick={() => onSort('status')}
        >
          Status
          <SortIndicator
            column="status"
            active={sortColumn}
            direction={sortDirection}
          />
        </SortableHeaderCell>
      </HeaderRow>
      <InfiniteLoader
        isRowLoaded={isRowLoaded}
        loadMoreRows={loadMoreRows}
        rowCount={rowCount}
        threshold={5}
      >
        {({ onRowsRendered, registerChild }) => {
          registerChildRef.current = registerChild;
          return (
            <AutoSizer disableHeight>
              {({ width }) => (
                <List
                  ref={listRefCallback}
                  onRowsRendered={onRowsRendered}
                  height={listHeight}
                  width={width}
                  rowCount={rowCount}
                  rowHeight={cellCache.rowHeight}
                  deferredMeasurementCache={cellCache}
                  rowRenderer={rowRenderer}
                  overscanRowCount={5}
                />
              )}
            </AutoSizer>
          );
        }}
      </InfiniteLoader>
    </Wrapper>
  );
};

export default React.memo(QueueList);

const Wrapper = styled.div`
  width: 100%;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid ${Color.BORDER_GREY};
  font-weight: 600;
  font-size: 14px;
`;

const HeaderCell = styled.div<{
  $flex: string;
  $centered?: boolean;
  $iconPadding?: boolean;
}>`
  flex: ${({ $flex }) => $flex};
  padding: 0 ${({ $iconPadding }) => ($iconPadding ? '18px' : '2px')};
  overflow: hidden;
  text-align: ${({ $centered }) => ($centered ? 'center' : 'left')};
`;

const SortableHeaderCell = styled(HeaderCell)<{ $active?: boolean }>`
  cursor: pointer;
  user-select: none;
  display: flex;
  align-items: center;
  gap: 4px;
  color: ${Color.TERTIARY_TEXT};

  &:hover {
    color: ${Color.PRIMARY};
  }

  ${({ $active }) => $active && `text-decoration: underline;`}
`;

const SortIndicator: React.FC<{
  column: SortColumn;
  active: SortColumn;
  direction: SortDirection;
}> = ({ column, active, direction }) => {
  if (column !== active) return null;
  return (
    <Icon
      icon={direction === 'asc' ? 'arrowUpOutlined' : 'arrowDownOutlined'}
      size={12}
      color="grey"
    />
  );
};

const Cell = styled.div<{ $flex: string }>`
  flex: ${({ $flex }) => $flex};
  padding: 0 4px;
  overflow: hidden;
  min-width: 0;
`;

const RowOuter = styled.div`
  cursor: pointer;
  border-bottom: 1px solid ${Color.BORDER_GREY};

  &.flagged-row {
    background-color: #ffc0c0;
  }

  &:hover {
    background-color: ${Color.LIGHTER_GREY};
  }

  &.flagged-row:hover {
    background-color: #ffb0b0;
  }

  & .show-on-viewing {
    opacity: 0;
  }

  &.showing-row .show-on-viewing {
    opacity: 1;
  }

  /* 
    required because show-on-hover-button won't be hidden when re-ordering rows 
    e.g flagging rows, resulting in flickering 
  */
  & .show-on-hover-button {
    opacity: 0;
    visibility: hidden;
    pointer-events: none;
  }

  &:hover .show-on-hover-button {
    opacity: 1;
    visibility: visible;
    pointer-events: auto;
  }

  &.is-reordering .show-on-hover-button {
    opacity: 0 !important;
    visibility: hidden !important;
    pointer-events: none !important;
  }
`;

const RowInner = styled.div`
  display: flex;
  align-items: center;
  height: 100%;
  padding: 4px 0;
`;

const LoadingRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
`;

const CenteredContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 300px;
`;
